<template>
  <div>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      top
      right
      timeout="4000"
    >
      {{ snackbar.message }}
    </v-snackbar>

    <div v-if="loading" style="height: 400px">
      <v-row class="fill-height" align-content="center" justify="center">
        <v-col class="subtitle-1 text-center" cols="12"
          >Loading Resources</v-col
        >
        <v-col cols="6">
          <v-progress-linear
            color="primary accent-4"
            indeterminate
            rounded
            height="6"
          />
        </v-col>
      </v-row>
    </div>

    <div v-else>
      <!-- ── Resources ─────────────────────────────────── -->
      <div class="section-header mb-4">
        <h2 class="text-h5 font-weight-bold">Resources</h2>
      </div>

      <v-card class="mb-8" outlined>
        <v-card-text class="pb-0">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="newEntry.title"
                label="Title"
                placeholder="e.g. New Roles & Permissions Feature"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <div
            class="caption grey--text text--darken-2 mb-2 font-weight-medium"
          >
            CONTENT
          </div>
          <content-editor v-model="newEntry.content" />

          <v-divider class="my-4" />

          <div
            class="caption grey--text text--darken-2 mb-1 font-weight-medium"
          >
            NOTIFICATION BANNER
          </div>
          <div class="caption grey--text mb-2">
            This text appears as a dismissable banner on the web app. "Publish
            Banner" sends it to all users — "Create Entry" will not.
          </div>
          <v-text-field
            v-model="newEntry.banner_text"
            label="Banner message (optional)"
            placeholder="e.g. New Roles & Permissions feature now available — check Resources"
            outlined
            dense
            counter="500"
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions class="px-4 py-4">
          <v-btn
            color="primary"
            :loading="saving && !publishingNew"
            @click="onSaveClick"
            large
          >
            Create Entry
          </v-btn>
          <v-btn
            outlined
            color="blue darken-1"
            :loading="publishingNew && saving"
            @click="onPublishClick"
            large
          >
            Publish Banner
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- ── Historical Resources ──────────────────────── -->
      <div
        class="section-header d-flex align-center justify-space-between mb-4"
      >
        <h2 class="text-h5 font-weight-bold">Historical Resources</h2>
        <v-btn-toggle v-model="sortOrder" mandatory dense>
          <v-btn value="desc" small>Newest First</v-btn>
          <v-btn value="asc" small>Oldest First</v-btn>
        </v-btn-toggle>
      </div>

      <div
        v-if="sortedEntries.length === 0"
        class="grey--text text-center py-8"
      >
        No updates published yet.
      </div>

      <v-card
        v-for="entry in sortedEntries"
        :key="entry.id"
        class="mb-4"
        outlined
      >
        <!-- View mode -->
        <template v-if="editingId !== entry.id">
          <v-card-title class="text-h5 font-weight-bold pb-2">
            {{ entry.title || 'Untitled Update' }}
          </v-card-title>
          <v-card-subtitle class="pt-2">
            Published {{ formatDate(entry.created_at) }}
            <span v-if="wasEdited(entry)">
              · Updated {{ formatDate(entry.updated_at) }}</span
            >
          </v-card-subtitle>
          <v-card-text>
            <div class="resource-content mb-3" v-html="entry.content" />
            <v-chip
              v-if="entry.banner_text"
              small
              color="blue lighten-4"
              text-color="blue darken-3"
            >
              Banner: {{ entry.banner_text }}
            </v-chip>
          </v-card-text>
          <v-card-actions>
            <v-btn text color="primary" @click="startEdit(entry)">Edit</v-btn>
            <v-btn
              text
              color="blue darken-1"
              :loading="republishingId === entry.id"
              @click="republish(entry)"
            >
              Republish Banner
            </v-btn>
            <v-spacer />
            <v-btn text color="error" @click="confirmDeleteEntry = entry"
              >Delete</v-btn
            >
          </v-card-actions>
        </template>

        <!-- Edit mode -->
        <template v-else>
          <v-card-text class="pb-0">
            <v-text-field
              v-model="editForm.title"
              label="Title"
              placeholder="e.g. New Roles & Permissions Feature"
              outlined
              dense
              hide-details="auto"
            />

            <v-divider class="my-4" />

            <div
              class="caption grey--text text--darken-2 mb-2 font-weight-medium"
            >
              CONTENT
            </div>
            <content-editor v-model="editForm.content" />

            <v-divider class="my-4" />

            <div
              class="caption grey--text text--darken-2 mb-1 font-weight-medium"
            >
              NOTIFICATION BANNER
            </div>
            <div class="caption grey--text mb-2">
              "Republish Banner" sends this text as a notification to all web
              users — "Save Changes" will not.
            </div>
            <v-text-field
              v-model="editForm.banner_text"
              label="Banner message (optional)"
              placeholder="e.g. Resources updated — check what's new"
              outlined
              dense
              counter="500"
              hide-details="auto"
            />
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-btn
              color="primary"
              :loading="savingEdit && !republishingOnSave"
              @click="saveEdit(entry.id)"
              >Save Changes</v-btn
            >
            <v-btn
              outlined
              color="blue darken-1"
              :loading="republishingOnSave && savingEdit"
              @click="saveAndRepublish(entry.id)"
              >Republish Banner</v-btn
            >
            <v-btn text @click="editingId = null">Cancel</v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </div>

    <!-- Confirm publish modal -->
    <ConfirmModal
      v-model="confirmCreate"
      title="Publish Update"
      message="This will create a new resource entry and send a banner notification to all web users."
      @cancel="confirmCreate = false"
      @confirm="createAndPublish"
    />

    <!-- Confirm delete modal -->
    <ConfirmModal
      v-if="confirmDeleteEntry"
      v-model="showDeleteModal"
      title="Delete Entry"
      :message="`Delete '${
        confirmDeleteEntry.title || 'this entry'
      }'? This cannot be undone.`"
      @cancel="confirmDeleteEntry = null"
      @confirm="deleteEntry"
    />
  </div>
</template>

<script>
import ContentEditor from '../PageManager/components/ContentEditor.vue'
import ConfirmModal from '@/components/modals/ConfirmModal'
import dayjs from 'dayjs'

export default {
  components: { ContentEditor, ConfirmModal },

  data() {
    return {
      loading: true,
      entries: [],
      sortOrder: 'desc',

      newEntry: { title: '', content: '', banner_text: '' },
      saving: false,
      publishingNew: false,
      confirmCreate: false,

      editingId: null,
      editForm: { title: '', content: '', banner_text: '' },
      savingEdit: false,
      republishingOnSave: false,

      confirmDeleteEntry: null,

      republishingId: null,

      snackbar: { show: false, message: '', color: 'success' },
    }
  },

  computed: {
    sortedEntries() {
      return [...this.entries].sort((a, b) => {
        const diff = new Date(b.created_at) - new Date(a.created_at)
        return this.sortOrder === 'desc' ? diff : -diff
      })
    },
    showDeleteModal() {
      return !!this.confirmDeleteEntry
    },
  },

  mounted() {
    this.loadEntries()
  },

  methods: {
    notify(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    },

    async loadEntries() {
      try {
        this.loading = true
        const { data } = await this.$api.get('admin/settings/pages/resources')
        this.entries = data.data || []
      } catch {
        this.notify('Failed to load entries.', 'error')
      } finally {
        this.loading = false
      }
    },

    hasNewEntryContent() {
      const hasContent =
        this.newEntry.content &&
        this.newEntry.content.replace(/<[^>]*>/g, '').trim() !== ''
      if (!hasContent) {
        this.notify('Please add some content before saving.', 'warning')
      }
      return hasContent
    },

    async onSaveClick() {
      if (!this.hasNewEntryContent()) return
      try {
        this.saving = true
        await this.$api.post('admin/settings/pages/resources', {
          title: this.newEntry.title || null,
          content: this.newEntry.content,
          banner_text: this.newEntry.banner_text || null,
        })
        this.newEntry = { title: '', content: '', banner_text: '' }
        this.notify('Entry created.')
        await this.loadEntries()
      } catch (e) {
        const msg =
          e?.response?.data?.message || 'Failed to save. Please try again.'
        this.notify(msg, 'error')
      } finally {
        this.saving = false
      }
    },

    onPublishClick() {
      if (!this.hasNewEntryContent()) return
      this.confirmCreate = true
    },

    async createAndPublish() {
      this.confirmCreate = false
      try {
        this.saving = true
        this.publishingNew = true
        const { data } = await this.$api.post(
          'admin/settings/pages/resources',
          {
            title: this.newEntry.title || null,
            content: this.newEntry.content,
            banner_text: this.newEntry.banner_text || null,
          },
        )
        const newId = data.data.id
        await this.$api.post(
          `admin/settings/pages/resources/${newId}/republish`,
        )
        this.newEntry = { title: '', content: '', banner_text: '' }
        this.notify('Update published successfully.')
        await this.loadEntries()
      } catch (e) {
        const msg =
          e?.response?.data?.message || 'Failed to publish. Please try again.'
        this.notify(msg, 'error')
      } finally {
        this.saving = false
        this.publishingNew = false
      }
    },

    startEdit(entry) {
      this.editingId = entry.id
      this.editForm = {
        title: entry.title || '',
        content: entry.content || '',
        banner_text: entry.banner_text || '',
      }
    },

    async saveEdit(id) {
      try {
        this.savingEdit = true
        await this.$api.put(`admin/settings/pages/resources/${id}`, {
          title: this.editForm.title || null,
          content: this.editForm.content,
          banner_text: this.editForm.banner_text || null,
        })
        this.editingId = null
        this.notify('Entry updated.')
        await this.loadEntries()
      } catch (e) {
        const msg =
          e?.response?.data?.message || 'Failed to save. Please try again.'
        this.notify(msg, 'error')
      } finally {
        this.savingEdit = false
      }
    },

    async saveAndRepublish(id) {
      try {
        this.savingEdit = true
        this.republishingOnSave = true
        await this.$api.put(`admin/settings/pages/resources/${id}`, {
          title: this.editForm.title || null,
          content: this.editForm.content,
          banner_text: this.editForm.banner_text || null,
        })
        await this.$api.post(`admin/settings/pages/resources/${id}/republish`)
        this.editingId = null
        this.notify('Entry updated and banner sent.')
        await this.loadEntries()
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          'Failed to save and publish banner. Please try again.'
        this.notify(msg, 'error')
      } finally {
        this.savingEdit = false
        this.republishingOnSave = false
      }
    },

    async republish(entry) {
      this.republishingId = entry.id
      try {
        await this.$api.post(
          `admin/settings/pages/resources/${entry.id}/republish`,
        )
        this.notify('Banner notification resent.')
      } catch {
        this.notify('Failed to republish banner.', 'error')
      } finally {
        this.republishingId = null
      }
    },

    async deleteEntry() {
      const entry = this.confirmDeleteEntry
      this.confirmDeleteEntry = null
      try {
        await this.$api.delete(`admin/settings/pages/resources/${entry.id}`)
        this.notify('Entry deleted.')
        await this.loadEntries()
      } catch {
        this.notify('Failed to delete entry.', 'error')
      }
    },

    formatDate(ts) {
      return dayjs(ts).format('D MMM YYYY, h:mm A')
    },

    wasEdited(entry) {
      return (
        entry.updated_at &&
        entry.created_at &&
        Math.abs(new Date(entry.updated_at) - new Date(entry.created_at)) > 5000
      )
    },
  },
}
</script>

<style scoped>
.resource-content ::v-deep img {
  max-width: 100%;
  height: auto;
}

/* Give the editor a visible minimum height so it looks like a text area */
::v-deep .tiptap-vuetify-editor__content .ProseMirror {
  min-height: 160px;
  padding: 12px;
}

/* Placeholder text when editor is empty */
::v-deep
  .tiptap-vuetify-editor__content
  .ProseMirror
  > p:only-child:empty::before {
  content: 'Write your resource content here…';
  color: rgba(0, 0, 0, 0.38);
  pointer-events: none;
}
</style>
